import {useMemo, useState} from 'react';
import {useLocation, useNavigate, useParams} from 'react-router-dom';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import Delete from '@mui/icons-material/Delete';
import {
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Link,
  Stack,
  Tooltip,
  Typography,
} from '@mui/material';
import {isAxiosError} from 'axios';
import {MRT_ColumnDef} from 'material-react-table';
import {useSnackbar} from 'notistack';

import {
  CreateEventGroupDialog,
  Event,
  EventGroupType,
  EventLink,
  EventSelect,
  EventStatus,
  GroupEvent,
  useConfirmEvents,
  useCreateEventGroup,
  useDeleteEventGroup,
  useGetEvent,
  useUpdateEventGroup,
} from '@entities/event';
import {useGetEventOrders} from '@entities/order';
import {getErrorMessageFromApiError} from '@shared/api';
import {
  CardBox,
  ClassicMuiNavTabs,
  CurrencyValue,
  MrTable,
  openConfirmation,
  rightAlignmentColumnProps,
  TableRowActionsContainer,
} from '@shared/components';
import {
  entityTableCommonProps,
  ROUTE__EVENT_DETAILS,
  ROUTE__EVENT_LOGS,
  ROUTE__EVENT_ORDERS,
  ROUTE__EVENT_STAKES,
} from '@shared/constants';
import {isLive} from '@shared/utils';
import {BaseWidgetLayout, Header} from '@widgets/common';
import {
  EditEventForm,
  EventParticipants,
  EventResolution,
} from '@widgets/event';
import {EventLogsTab} from './EventLogsTab';
import {EventOrdersTab} from './EventOrdersTab';
import {EventStakesTab} from './EventStakesTab';

import {COLOR__WHITE} from '@/theme/colors';

export const EditEventPage = () => {
  const {event_id} = useParams<{event_id: string}>();
  const {pathname} = useLocation();
  const navigate = useNavigate();
  const {enqueueSnackbar} = useSnackbar();
  const {mutateAsync: confirmEvents, isPending: isConfirmingEvents} =
    useConfirmEvents();
  const {mutateAsync: deleteEventGroup} = useDeleteEventGroup();
  const {mutateAsync: createEventGroup, isPending: isCreatingGroup} =
    useCreateEventGroup();
  const {mutateAsync: updateEventGroup, isPending: isAddingToGroup} =
    useUpdateEventGroup();

  const [isCreateDialogOpen, setIsCreateDialogOpen] = useState(false);
  const [isAddDialogOpen, setIsAddDialogOpen] = useState(false);
  const [selectedEvent, setSelectedEvent] = useState<Event | null>(null);

  const {data, isPending, error} = useGetEvent(event_id ?? '', {
    enabled: !!event_id,
  });
  const {
    data: eventOrders,
    isPending: isLoadingEventOrders,
    isError: isEventOrdersError,
  } = useGetEventOrders(
    {eventId: event_id ?? '', page: 1, pageSize: 1},
    {enabled: data?.status === EventStatus.active},
  );

  const groupId = data?.groupId;

  const columns = useMemo<MRT_ColumnDef<GroupEvent>[]>(
    () => [
      {
        accessorKey: 'eventId',
        header: 'ID',
        Cell: ({row}) => {
          const {eventId} = row.original;

          return <EventLink eventId={eventId} />;
        },
      },
      {
        accessorKey: 'name',
        header: 'Name',
      },
      {
        accessorKey: 'shortName',
        header: 'Short name',
      },
      {
        id: 'group',
        header: 'Group',
        size: 120,
        Cell: () => {
          if (!groupId) {
            return '-';
          }

          return (
            <Tooltip title={`${groupId} (click to copy)`}>
              <Link
                component="button"
                type="button"
                onClick={() => {
                  void navigator.clipboard.writeText(groupId);
                  enqueueSnackbar('Group ID copied', {variant: 'success'});
                }}
              >
                {groupId.slice(0, 6)}
              </Link>
            </Tooltip>
          );
        },
      },
      {
        accessorKey: 'yesPrice',
        header: 'Yes price',
        Cell: ({row}) => {
          return <CurrencyValue value={row.original.yesPrice} />;
        },
        maxSize: 160,
        ...rightAlignmentColumnProps,
      },
      {
        accessorKey: 'noPrice',
        header: 'No price',
        Cell: ({row}) => {
          return <CurrencyValue value={row.original.noPrice} />;
        },
        maxSize: 160,
        ...rightAlignmentColumnProps,
      },
    ],
    [groupId, enqueueSnackbar],
  );

  const handleClose = () => {
    navigate(-1);
  };

  const handleDeleteGroup = async (eventSlug: string) => {
    try {
      await openConfirmation({
        title: 'Delete group?',
        content: (
          <>
            <Typography>
              Are you sure you want to delete this event group?
            </Typography>
            <br />
            <Typography>This action can not be undone!</Typography>
          </>
        ),
        okButtonText: 'Delete',
      });
    } catch {
      return;
    }

    try {
      await deleteEventGroup({eventSlugs: [eventSlug]});
      enqueueSnackbar('Group deleted successfully', {variant: 'success'});
    } catch (err) {
      const errorMessage = isAxiosError(err)
        ? getErrorMessageFromApiError(err)
        : '';

      enqueueSnackbar(`Could not delete group. ${errorMessage}`, {
        variant: 'error',
      });
    }
  };

  const handleCreateGroup = async (groupType: EventGroupType) => {
    if (!data) {
      return;
    }

    try {
      await createEventGroup({eventSlugs: [data.eventId], groupType});
      setIsCreateDialogOpen(false);
      enqueueSnackbar('Group created successfully', {variant: 'success'});
    } catch (err) {
      const errorMessage = isAxiosError(err)
        ? getErrorMessageFromApiError(err)
        : '';

      enqueueSnackbar(`Could not create group. ${errorMessage}`, {
        variant: 'error',
      });
    }
  };

  const handleAddToGroup = async () => {
    if (!data || !selectedEvent) {
      return;
    }

    if (!groupId) {
      enqueueSnackbar('This event has no group', {variant: 'error'});
      return;
    }

    const eventSlugs = [
      ...new Set([
        data.eventId,
        ...(data.group ?? []).map(g => g.eventId),
        selectedEvent.eventId,
      ]),
    ];

    try {
      await updateEventGroup({groupId, eventSlugs});
      setIsAddDialogOpen(false);
      setSelectedEvent(null);
      enqueueSnackbar('Event added to group successfully', {
        variant: 'success',
      });
    } catch (err) {
      const errorMessage = isAxiosError(err)
        ? getErrorMessageFromApiError(err)
        : '';

      enqueueSnackbar(`Could not add event to group. ${errorMessage}`, {
        variant: 'error',
      });
    }
  };

  const handlePublishEvent = async () => {
    if (!data || data.status !== EventStatus.null) {
      return;
    }

    try {
      await confirmEvents({
        eventSlugs: [data.eventId],
        confirm: true,
      });
      enqueueSnackbar('Event published successfully', {
        variant: 'success',
      });
    } catch (err) {
      const errorMessage = isAxiosError(err)
        ? getErrorMessageFromApiError(err)
        : '';

      enqueueSnackbar(`Could not publish event. ${errorMessage}`, {
        variant: 'error',
      });
    }
  };

  const handleUnpublishEvent = async () => {
    if (!data || data.status !== EventStatus.active || eventOrders?.total) {
      return;
    }

    try {
      await confirmEvents({
        eventSlugs: [data.eventId],
        confirm: false,
      });
      enqueueSnackbar('Event unpublished successfully', {
        variant: 'success',
      });
    } catch (err) {
      const errorMessage = isAxiosError(err)
        ? getErrorMessageFromApiError(err)
        : '';

      enqueueSnackbar(`Could not unpublish event. ${errorMessage}`, {
        variant: 'error',
      });
    }
  };

  if (!event_id) {
    return 'Invalid event ID';
  }

  const hasGroupEvents = (data?.group ?? []).length > 0;
  const isUnpublishDisabled =
    isConfirmingEvents ||
    isLoadingEventOrders ||
    isEventOrdersError ||
    data?.status !== EventStatus.active ||
    Boolean(eventOrders?.total);
  const informationRoute = ROUTE__EVENT_DETAILS.replace(':event_id', event_id);
  const logsRoute = ROUTE__EVENT_LOGS.replace(':event_id', event_id);
  const ordersRoute = ROUTE__EVENT_ORDERS.replace(':event_id', event_id);
  const stakesRoute = ROUTE__EVENT_STAKES.replace(':event_id', event_id);
  const isLogsTab = pathname === logsRoute;
  const isOrdersTab = pathname === ordersRoute;
  const isStakesTab = pathname === stakesRoute;
  const tabs = [
    {route: informationRoute, label: 'Information'},
    {route: logsRoute, label: 'Logs'},
    {route: ordersRoute, label: 'Orders'},
    {route: stakesRoute, label: 'Stakes'},
  ];

  return (
    <>
      <Header
        title={`Event ${event_id}`}
        mb={1}
        leftPart={
          <Box display="flex" alignItems="center" gap={1}>
            {data && (
              <Tooltip title="Copy event ID">
                <Box
                  display="flex"
                  alignItems="center"
                  gap={0.5}
                  sx={{cursor: 'pointer'}}
                  onClick={() => {
                    void navigator.clipboard.writeText(data.id);
                    enqueueSnackbar('Event ID copied', {variant: 'success'});
                  }}
                >
                  <Typography color="text.secondary" sx={{fontSize: 13}}>
                    ID: {data.id}
                  </Typography>
                  <ContentCopyIcon sx={{fontSize: 14}} color="action" />
                </Box>
              </Tooltip>
            )}
            {data && isLive(data.startDate, data.endDate, Date.now()) && (
              <Chip
                label="LIVE"
                color="error"
                size="small"
                sx={{fontWeight: 600}}
              />
            )}
            {data && (
              <Box display="flex" gap={1} ml="18px">
                <Button
                  onClick={() => {
                    void handlePublishEvent();
                  }}
                  disabled={
                    isConfirmingEvents || data.status !== EventStatus.null
                  }
                >
                  {data.status === EventStatus.null
                    ? 'Publish'
                    : 'Already published'}
                </Button>
                <Button
                  color="error"
                  onClick={() => {
                    void handleUnpublishEvent();
                  }}
                  disabled={isUnpublishDisabled}
                >
                  Unpublish
                </Button>
              </Box>
            )}
          </Box>
        }
      />

      <BaseWidgetLayout isPending={isPending} error={error}>
        {data && (
          <>
            <CardBox bgcolor={COLOR__WHITE} mb={3}>
              <ClassicMuiNavTabs tabs={tabs} />
            </CardBox>

            {isLogsTab ? (
              <CardBox bgcolor={COLOR__WHITE}>
                <EventLogsTab eventSlug={event_id} />
              </CardBox>
            ) : isOrdersTab ? (
              <CardBox bgcolor={COLOR__WHITE}>
                <EventOrdersTab eventSlug={event_id} />
              </CardBox>
            ) : isStakesTab ? (
              <CardBox bgcolor={COLOR__WHITE}>
                <EventStakesTab eventId={data.id} />
              </CardBox>
            ) : (
              <>
                <CardBox bgcolor={COLOR__WHITE} mb={3}>
                  <EditEventForm event={data} onClose={handleClose} />
                </CardBox>

                <CardBox bgcolor={COLOR__WHITE} mb={3}>
                  <EventParticipants event={data} />
                </CardBox>

                <CardBox bgcolor={COLOR__WHITE} mb={3}>
                  <EventResolution event={data} />
                </CardBox>

                <CardBox bgcolor={COLOR__WHITE} mb={3}>
                  <Stack direction="row" spacing={2}>
                    <Typography>
                      Holders: <strong>{data.holders}</strong>
                    </Typography>
                    <Typography>
                      Participants: <strong>{data.participants}</strong>
                    </Typography>
                  </Stack>
                </CardBox>

                <CardBox bgcolor={COLOR__WHITE}>
                  <Header
                    title="Group events"
                    mb={2}
                    isBackButtonShown={false}
                  />
                  <MrTable
                    columns={columns}
                    data={data.group ?? []}
                    renderTopToolbarCustomActions={() => (
                      <Box display="flex" gap={1}>
                        <Button
                          onClick={() => {
                            setIsCreateDialogOpen(true);
                          }}
                        >
                          Create group
                        </Button>
                        <Button
                          variant="outlined"
                          disabled={!hasGroupEvents}
                          onClick={() => {
                            setIsAddDialogOpen(true);
                          }}
                        >
                          Add to group
                        </Button>
                      </Box>
                    )}
                    renderRowActions={({row}) => (
                      <TableRowActionsContainer>
                        <Tooltip title="Delete group">
                          <IconButton
                            size="small"
                            color="error"
                            onClick={() =>
                              void handleDeleteGroup(row.original.eventId)
                            }
                          >
                            <Delete fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </TableRowActionsContainer>
                    )}
                    {...entityTableCommonProps}
                  />
                </CardBox>
              </>
            )}
          </>
        )}
      </BaseWidgetLayout>

      <CreateEventGroupDialog
        open={isCreateDialogOpen}
        onClose={() => setIsCreateDialogOpen(false)}
        onCreate={groupType => {
          void handleCreateGroup(groupType);
        }}
        isCreating={isCreatingGroup}
      />

      <Dialog
        open={isAddDialogOpen}
        onClose={() => setIsAddDialogOpen(false)}
        slotProps={{paper: {sx: {width: 420, p: 2}}}}
      >
        <DialogTitle>Add to group</DialogTitle>
        <DialogContent>
          <Box pt={1}>
            <EventSelect
              label="Event"
              value={selectedEvent}
              onChange={setSelectedEvent}
              fullWidth
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button
            variant="outlined"
            onClick={() => setIsAddDialogOpen(false)}
            disabled={isAddingToGroup}
          >
            Cancel
          </Button>
          <Button
            onClick={() => {
              void handleAddToGroup();
            }}
            disabled={isAddingToGroup || !selectedEvent}
          >
            Add
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};
